import { styled } from "styled-components";

import { ErrorMessage, LinkIcon, Spinner } from "common/components";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getCurrentMap } from "domains/createMapDetail/selectors";
import { AccessListItem } from "domains/createShareSettings/components/AccessListItem";
import { ShareSettingsHeader } from "domains/createShareSettings/components/ShareSettingsHeader";
import { UserAccessListItem } from "domains/createShareSettings/components/UserAccessListItem";
import {
  getCollaborators,
  getCollaboratorsError,
  getCollaboratorsPending,
  getInvitees,
} from "domains/createShareSettings/selectors";

const StyledLinkContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  min-width: 34px;
  height: 34px;
  border-radius: 17px;
  background-color: ${({ theme }) => theme.colors.linkIconBackground};
`;

const StyledShareSettingsHeader = styled(ShareSettingsHeader)`
  padding-top: 1rem;
`;

const StyledLinkIcon = styled(LinkIcon)`
  width: 30px;
  height: 30px;
  color: ${({ theme }) => theme.colors.backgroundSecondary};
`;

const StyledLoadingIndicator = styled(Spinner)`
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-left: 20px;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin: 12px 0 0 0;

  p {
    margin: 12px;
  }
`;

const StyledListContainer = styled.div`
  margin-top: 1rem;
`;

type AccessListProps = { mapId: string; publicAccess: boolean };

export const AccessList = ({ publicAccess, mapId }: AccessListProps) => {
  const translate = useTranslate();
  const map = useSelector(getCurrentMap);
  const accessListError = useSelector(getCollaboratorsError);
  const isPending = useSelector(getCollaboratorsPending);
  const collaborators = useSelector(getCollaborators);
  const invitees = useSelector(getInvitees);

  if (!map) {
    return null;
  }

  if (accessListError) {
    return <StyledErrorMessage isActive>{translate("app.error.unknown.title")}</StyledErrorMessage>;
  }

  return (
    <>
      <ShareSettingsHeader>{translate("app.create.share_settings.access_list.title")}</ShareSettingsHeader>

      {isPending ? (
        <StyledLoadingIndicator />
      ) : (
        <StyledListContainer>
          {collaborators.map((user) => (
            <UserAccessListItem key={user.email} user={user} topicId={mapId} />
          ))}
          {publicAccess && (
            <AccessListItem
              key={translate("app.create.share_settings.public_access")}
              title={translate("app.create.share_settings.public_access")}
              icon={
                <StyledLinkContainer>
                  <StyledLinkIcon />
                </StyledLinkContainer>
              }
              permission={translate("app.create.share_settings.read_only")}
              editablePermission={false}
            />
          )}
        </StyledListContainer>
      )}
      {invitees.length !== 0 && (
        <>
          <StyledShareSettingsHeader>{translate("app.create.share_settings.invited_users")}</StyledShareSettingsHeader>
          <StyledListContainer>
            {invitees.map((user) => (
              <UserAccessListItem key={user.email} user={user} topicId={mapId} />
            ))}
          </StyledListContainer>
        </>
      )}
    </>
  );
};
